import type { SiteSyncState } from '@epc/contracts';

export interface OfflineScope {
  tenantId: string;
  userId: string;
  deviceId: string;
}
export interface QueuedCommand {
  clientOperationId: string;
  kind: 'create' | 'action';
  action: 'create' | 'save' | 'submit';
  method: 'POST' | 'PATCH';
  path: string;
  requiredPhotos: Array<{ clientPhotoId: string; fileId: string; sha256: string; size: number }>;
  /** После постановки тело команды не меняется, включая expectedRevision. */
  requestJson: string;
  expectedRevision: number;
  localRevision: number;
  state: SiteSyncState;
  attempts: number;
  nextAttemptAt: number;
  lease: { token: string; until: number } | null;
  error: { code: string; message: string } | null;
  conflict: { revision: number; state: string; server: unknown; base: unknown } | null;
  resolvedBy: string | null;
  ack: { reportId: string; revision: number } | null;
}
export interface LocalPhoto {
  clientPhotoId: string;
  /** Подготовленный upstream-файловым слоем Blob; очистка EXIF не является задачей очереди. */
  blob: Blob;
  sha256: string;
  size: number;
  state: SiteSyncState;
  uploadId: string | null;
  parts: Record<string, string>;
  fileId: string | null;
}
export interface LocalSiteReport {
  key: string;
  scope: string;
  schemaVersion: 1;
  tenantId: string;
  userId: string;
  deviceId: string;
  clientId: string;
  localRevision: number;
  revision: number;
  syncedLocalRevision: number;
  serverId: string | null;
  serverSnapshot: unknown;
  /** Сохраняется также незавершённый ввод, не прошедший серверную валидацию. */
  draft: unknown;
  savedAt: number;
  authPaused: boolean;
  commands: QueuedCommand[];
  photos: LocalPhoto[];
  /** Пользователь нажал «Отправить»: фото, сохранение и отправка на проверку пойдут сами, когда появится связь */
  autoSend?: boolean;
}

export const scopeKey = (scope: OfflineScope) =>
  JSON.stringify([scope.tenantId, scope.userId, scope.deviceId]);
export const reportKey = (scope: OfflineScope, clientId: string) =>
  JSON.stringify([scope.tenantId, scope.userId, scope.deviceId, clientId]);

export class SiteOfflineError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'SiteOfflineError';
  }
}

/** Все изменения одной записи сериализуются транзакцией IndexedDB, включая несколько вкладок. */
export class SiteOfflineStore {
  constructor(private readonly db: IDBDatabase) {}

  static open(factory: IDBFactory = indexedDB, name = 'epc-site-offline'): Promise<SiteOfflineStore> {
    return new Promise((resolve, reject) => {
      const request = factory.open(name, 1);
      let blocked = false;
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains('reports')) {
          const store = request.result.createObjectStore('reports', { keyPath: 'key' });
          store.createIndex('scope', 'scope');
        }
      };
      request.onblocked = () => {
        blocked = true;
        reject(new SiteOfflineError('upgrade_blocked', 'Закройте другую вкладку для обновления хранилища'));
      };
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        if (blocked) {
          request.result.close();
          return;
        }
        request.result.onversionchange = () => request.result.close();
        resolve(new SiteOfflineStore(request.result));
      };
    });
  }

  close() {
    this.db.close();
  }

  read(scope: OfflineScope, clientId: string): Promise<LocalSiteReport | null> {
    return this.run(scope, clientId, 'readonly', (row) => ({ row, result: row }));
  }

  list(scope: OfflineScope): Promise<LocalSiteReport[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('reports', 'readonly');
      const request = tx.objectStore('reports').index('scope').getAll(scopeKey(scope));
      tx.oncomplete = () =>
        resolve(
          (request.result as LocalSiteReport[])
            .filter(
              (row) =>
                row.schemaVersion === 1 &&
                row.tenantId === scope.tenantId &&
                row.userId === scope.userId &&
                row.deviceId === scope.deviceId,
            )
            .sort((a, b) => b.savedAt - a.savedAt),
        );
      tx.onabort = () => reject(tx.error);
      tx.onerror = () => {};
    });
  }

  mutate<T>(
    scope: OfflineScope,
    clientId: string,
    fn: (row: LocalSiteReport | null) => { row: LocalSiteReport; result: T },
  ): Promise<T> {
    return this.run(scope, clientId, 'readwrite', fn);
  }

  private run<T>(
    scope: OfflineScope,
    clientId: string,
    mode: IDBTransactionMode,
    fn: (row: LocalSiteReport | null) => { row: LocalSiteReport | null; result: T },
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('reports', mode);
      const store = tx.objectStore('reports');
      const request = store.get(reportKey(scope, clientId));
      let result: T;
      let failure: unknown;
      request.onsuccess = () => {
        try {
          const row = (request.result ?? null) as LocalSiteReport | null;
          if (
            row &&
            (row.schemaVersion !== 1 ||
              row.scope !== scopeKey(scope) ||
              row.tenantId !== scope.tenantId ||
              row.userId !== scope.userId ||
              row.deviceId !== scope.deviceId ||
              row.clientId !== clientId)
          )
            throw new SiteOfflineError('invalid_storage', 'Хранилище требует проверки версии и пользователя');
          const updated = fn(row);
          result = updated.result;
          if (mode === 'readwrite' && updated.row) {
            if (
              updated.row.schemaVersion !== 1 ||
              updated.row.key !== reportKey(scope, clientId) ||
              updated.row.scope !== scopeKey(scope) ||
              updated.row.tenantId !== scope.tenantId ||
              updated.row.userId !== scope.userId ||
              updated.row.deviceId !== scope.deviceId ||
              updated.row.clientId !== clientId
            )
              throw new SiteOfflineError('invalid_scope', 'Нельзя изменить владельца черновика');
            store.put(updated.row);
          }
        } catch (error) {
          failure = error;
          tx.abort();
        }
      };
      tx.oncomplete = () => resolve(result);
      tx.onabort = () =>
        reject(failure ?? tx.error ?? new SiteOfflineError('storage_aborted', 'Черновик не сохранён'));
      tx.onerror = () => {
        /* Ошибка транзакции возвращается только после abort. */
      };
    });
  }
}
